import * as React from 'react';
import { useState, useRef } from 'react';
import * as util from './util';

import String from './FieldTypes/string';
import Address from './FieldTypes/address';
import Name from './FieldTypes/name';
import SingleChoiceList from './FieldTypes/singleChoiceList';
import MultiChoiceList from './FieldTypes/multiChoiceList';
import Date from './FieldTypes/date';
import Decimal from './FieldTypes/decimal';
import Email from './FieldTypes/email';
import TimeZone from "./FieldTypes/timeZone";
import Number from "./FieldTypes/number";
import File from './FieldTypes/file';
import DatetimeTimezone from "./FieldTypes/datetimetimezone";
import Icon from '../../icons';

const parseFieldSettings = (settings) => (settings ? JSON.parse(settings) : {});

// Field type resolution
const resolveTimezoneValue = (existingValue, fieldSetting, userTimeZoneValue, timeZoneValue) => {
    if (existingValue) return existingValue;
    if (fieldSetting?.timezone_from?.includes('user')) {
        return userTimeZoneValue || timeZoneValue;
    }
    return timeZoneValue;
};

// Builds the merged data/props for the 'timezone' field type.
const buildTimezoneFieldProps = (props) => {
    const fieldSetting = parseFieldSettings(props.data?.settings);
    const tzList = props.tzData;

    let mergedData = props.data;
    if (props?.timeZoneValue?.trim() !== "") {
        mergedData = {
            ...props.data,
            CustomFieldValues: tzList,
            value: resolveTimezoneValue(props.data?.value, fieldSetting, props?.userTimeZoneValue, props?.timeZoneValue),
            Label: props.renderLocation === "create" ? "" : props.data?.Label
        };
    }

    return {
        ...props,
        data: mergedData,
        timeZoneValue: resolveTimezoneValue(mergedData?.value, fieldSetting, props?.userTimeZoneValue, props.timeZoneValue)
    };
};

const getFieldComponent = (type, props) => {
    switch (type) {
        case 'string':
            return <String {...props} />;
        case 'address':
            return <Address {...props} />;
        case 'name':
            return <Name {...props} />;
        case 'singlechoicelist':
            return <SingleChoiceList {...props} />;
        case 'timezone': {
            const fieldSetting = parseFieldSettings(props.data?.settings);
            if (!fieldSetting?.input_timezone_component) return null;
            return <SingleChoiceList {...buildTimezoneFieldProps(props)} />;
        }
        case 'multiplechoicelist':
            return <MultiChoiceList {...props} />;
        case "decimal":
            return <Decimal {...props} />;
        case "date":
        case "datetime":
            return <Date {...props} />;
        case "email":
            return <Email {...props} />;
        case "datewithusertimezone":
        case "DateWithUserTimeZone":
            return <TimeZone {...props} />;
        case "number":
            return <Number {...props} />;
        case "file":
            return <File {...props} />;
        case 'datetimetimezone':
            return <DatetimeTimezone {...props} />;
        default:
            return undefined;
    }
};

// Drag-and-drop reordering (pinned fields)
const FieldRenderingEngine = (props) => {
    const { excludeFields = [], includeFields = [], dragndrop, customData, data, shouldupdate, updateUserCustomData } = props;
    const isPinnedData = dragndrop || false;

    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const dragOverItemPosition = useRef(null);
    const animationTarget = useRef(null);

    const [pinnedGroupData, setPinnedGroupData] = useState(null);

    const isValidField = (fieldGroup) => {
        if (includeFields.length > 0) return includeFields.includes(fieldGroup);
        return !excludeFields.includes(fieldGroup);
    };

    const dragStart = (e, position) => {
        dragItem.current = position;
    };

    const dragEnter = (e, position) => {
        e.preventDefault();
        dragOverItem.current = position;

        const draggedItem = dragItem.current;
        const draggedOverItem = dragOverItem.current;

        if (dragOverItemPosition.current === draggedOverItem) return;
        dragOverItemPosition.current = draggedOverItem;

        if (animationTarget.current != null) {
            animationTarget.current.style.transition = "";
            animationTarget.current.style.transform = "";
        }

        if (draggedItem == null || draggedOverItem == null || draggedItem === draggedOverItem) return;

        const element1 = document.getElementById(draggedItem);
        let element2 = document.getElementById(draggedOverItem);
        if (!element1 || !element2) return;
        element1.classList.add('dragslide');

        if (element1.parentNode !== element2.parentNode) return;

        element2 = element2 !== element1.nextElementSibling ? element2 : element1.nextElementSibling;
        if (element1 !== element2) {
            moveWithAnimation(element1, element2);
        }
    };

    const moveWithAnimation = (target, dropTarget) => {
        const moveDirection = !dropTarget || dropTarget.previousElementSibling === target.nextElementSibling;
        animationTarget.current = dropTarget;

        animationTarget.current.style.transform = `translateY(${moveDirection ? '-100%' : '100%'})`;
        animationTarget.current.style.transition = "transform .3s";
    };

    const drop = () => {
        if (animationTarget.current != null) {
            animationTarget.current.style.transition = "";
            animationTarget.current.style.transform = "";
        }
        document.getElementById(dragItem.current)?.classList.remove('dragslide');

        const copyListItems = [...pinnedGroupData];
        const dragItemContent = copyListItems[dragItem.current];
        copyListItems.splice(dragItem.current, 1);
        copyListItems.splice(dragOverItem.current, 0, dragItemContent);

        if (dragItem.current !== dragOverItem.current) {
            shouldupdate();
        }

        dragItem.current = null;
        dragOverItem.current = null;
        setPinnedGroupData(copyListItems);
        updateUserCustomData(copyListItems);
    };

    const renderPinnedField = (resultHtml, index) => (
        <div
            id={index}
            key={index}
            draggable
            onDragOver={(e) => e.preventDefault()}
            onDragStart={(e) => dragStart(e, index)}
            onDragEnter={(e) => dragEnter(e, index)}
            onDragEnd={drop}
            className="alignMiddle dragWorkkard reArrange"
        >
            <Icon name="drag" className="icon" />
            <span>{resultHtml}</span>
        </div>
    );

    const renderFields = (fieldsData, isPinned) => (
        <React.Fragment>
            {fieldsData.map((item, index) => {
                const fieldGroup = item[0].FieldGroup;
                if (!isValidField(fieldGroup)) return null;
                const group = item[0].Group ? item[0].Group.toLowerCase() : 'string';
                const resultHtml = getFieldComponent(group, {
                    ...props,
                    data: item,
                    key: index
                });

                if (resultHtml == null) return null;

                return isPinned ? renderPinnedField(resultHtml, index) : resultHtml;
            })}
        </React.Fragment>
    );

    const renderCustomData = () => (data || []).map((item, index) =>
        getFieldComponent(item.group, {
            ...props,
            data: item.data,
            key: index
        })
    );

    if (customData) {
        return renderCustomData();
    }

    const groupedData = util.groupedData(data);
    const excludeSet = new Set(excludeFields.map((f) => `${f || ''}`.trim().toLowerCase()));
    const isExcluded = (field) => [field.FieldGroup, field.Name, field.Label]
        .some((v) => v && excludeSet.has(`${v}`.trim().toLowerCase()));
    const filteredGroupData = groupedData.filter((x) => !isExcluded(x[0]));

    if (isPinnedData) {
        const activeGroupData = pinnedGroupData ?? filteredGroupData;
        if (pinnedGroupData == null) {
            setPinnedGroupData(filteredGroupData);
        }
        return renderFields(activeGroupData, isPinnedData);
    }

    if (pinnedGroupData != null) {
        setPinnedGroupData(null);
    }
    return renderFields(filteredGroupData, isPinnedData);
};

export default React.memo(FieldRenderingEngine);
